import type { CSSProperties, ReactNode } from "react";
import { AuthLayout, type AuthLayoutProps } from "@/components/brand/auth-layout";
import { cx } from "@/components/ui/cx";
import styles from "./auth-journey.module.css";

export type AuthOutcomeKind =
  | "form"
  | "progress"
  | "success"
  | "recoverable"
  | "terminal";

export interface AuthJourneyProps
  extends Omit<AuthLayoutProps, "className" | "children"> {
  outcome?: AuthOutcomeKind;
  currentStep?: number;
  totalSteps?: number;
  stepLabel?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Shared shell for every authentication journey.
 *
 * `outcome` communicates meaning through a labelled card rail and the child
 * content, never colour alone. Progress is rendered consistently when a flow
 * has multiple steps.
 */
export function AuthJourney({
  outcome = "form",
  currentStep,
  totalSteps,
  stepLabel,
  className,
  children,
  ...layoutProps
}: AuthJourneyProps) {
  const hasProgress =
    typeof currentStep === "number" &&
    typeof totalSteps === "number" &&
    totalSteps > 1;

  return (
    <AuthLayout
      {...layoutProps}
      className={cx(
        styles.journey,
        outcome === "success" && styles.outcomeSuccess,
        outcome === "recoverable" && styles.outcomeRecoverable,
        outcome === "terminal" && styles.outcomeTerminal,
        className,
      )}
      banner={
        <>
          {hasProgress ? (
            <AuthJourneyProgress
              current={currentStep}
              total={totalSteps}
              label={stepLabel}
            />
          ) : null}
          {layoutProps.banner}
        </>
      }
    >
      <div data-outcome={outcome}>{children}</div>
    </AuthLayout>
  );
}

export function AuthJourneyProgress({
  current,
  total,
  label,
}: {
  current: number;
  total: number;
  label?: string;
}) {
  const safeTotal = Math.max(1, Math.floor(total));
  const safeCurrent = Math.min(safeTotal, Math.max(1, Math.floor(current)));

  return (
    <div
      className={styles.progress}
      aria-label={`Step ${safeCurrent} of ${safeTotal}${
        label ? `: ${label}` : ""
      }`}
    >
      <div className={styles.progressLabel}>
        <span>
          Step {safeCurrent} of {safeTotal}
        </span>
        {label ? <span>{label}</span> : null}
      </div>
      <div
        className={styles.progressTrack}
        style={{ "--auth-step-count": safeTotal } as CSSProperties}
        aria-hidden="true"
      >
        {Array.from({ length: safeTotal }, (_, index) => (
          <span
            className={styles.progressStep}
            data-complete={index < safeCurrent}
            key={index}
          />
        ))}
      </div>
    </div>
  );
}

export { styles as authJourneyStyles };
